<script>
// NOTICE: KAPTION-INTEGRATION
import { mapGetters } from 'vuex';
import NextButton from 'dashboard/components-next/button/Button.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import SettingsLayout from '../SettingsLayout.vue';
import kaptionResponsePoliciesAPI from 'dashboard/api/kaptionResponsePolicies';

const DATE_RANGE_OPTIONS = [
  { id: 'last_7_days', name: 'Last 7 days', days: 7 },
  { id: 'last_30_days', name: 'Last 30 days', days: 30 },
  { id: 'last_90_days', name: 'Last 90 days', days: 90 },
];

export default {
  components: {
    SettingsLayout,
    NextButton,
    BaseSettingsHeader,
  },
  data() {
    return {
      selectedDateRange: DATE_RANGE_OPTIONS[0],
      selectedInbox: null,
      selectedTeam: null,
      selectedAgent: null,
      selectedPolicy: null,
      page: 1,
    };
  },
  computed: {
    ...mapGetters({
      policies: 'kaptionResponsePolicies/getPolicies',
      metrics: 'kaptionResponsePolicies/getMetrics',
      missed: 'kaptionResponsePolicies/getMissed',
      uiFlags: 'kaptionResponsePolicies/getUIFlags',
    }),
    dateRangeOptions() {
      return DATE_RANGE_OPTIONS;
    },
    inboxes() {
      return this.$store.getters['inboxes/getInboxes'] || [];
    },
    teams() {
      return this.$store.getters['teams/getTeams'] || [];
    },
    agents() {
      return this.$store.getters['agents/getAgents'] || [];
    },
    missedData() {
      return this.missed.data || [];
    },
    missedMeta() {
      return this.missed.meta || {};
    },
    totalPages() {
      const total = this.missedMeta.total || 0;
      return Math.ceil(total / 25) || 1;
    },
    filterParams() {
      const days = this.selectedDateRange?.days || 7;
      const since = new Date();
      since.setDate(since.getDate() - days);
      const params = { since: since.toISOString() };
      if (this.selectedInbox) params.inbox_id = this.selectedInbox.id;
      if (this.selectedTeam) params.team_id = this.selectedTeam.id;
      if (this.selectedAgent) params.assigned_agent_id = this.selectedAgent.id;
      if (this.selectedPolicy) params.policy_id = this.selectedPolicy.id;
      return params;
    },
    isLoading() {
      return this.uiFlags.fetchingMetrics || this.uiFlags.fetchingMissed;
    },
  },
  mounted() {
    this.$store.dispatch('kaptionResponsePolicies/get');
    this.$store.dispatch('inboxes/get');
    this.$store.dispatch('teams/get');
    this.$store.dispatch('agents/get');
    this.fetchData();
  },
  methods: {
    fetchData() {
      const params = this.filterParams;
      this.$store.dispatch('kaptionResponsePolicies/getMetrics', params);
      this.$store.dispatch('kaptionResponsePolicies/getMissed', {
        ...params,
        page: this.page,
      });
    },
    onDateRangeChange() {
      this.page = 1;
      this.fetchData();
    },
    onFilterChange() {
      this.page = 1;
      this.fetchData();
    },
    navigateToPolicies() {
      this.$router.push({
        name: 'settings_kaption_response_policies',
      });
    },
    goToPage(p) {
      if (p < 1 || p > this.totalPages) return;
      this.page = p;
      this.$store.dispatch('kaptionResponsePolicies/getMissed', {
        ...this.filterParams,
        page: this.page,
      });
    },
    async downloadCSV() {
      try {
        const response = await kaptionResponsePoliciesAPI.download(
          this.filterParams
        );
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `sla-report-${Date.now()}.csv`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      } catch {
        // Silently handle error
      }
    },
    breachedBadgeClass(type) {
      const classes = {
        frt: 'bg-r-alpha-2 text-r-solid',
        nrt: 'bg-w-alpha-2 text-w-solid',
        rt: 'bg-s-alpha-2 text-s-solid',
      };
      return classes[type] || 'bg-n-alpha-2 text-n-slate-11';
    },
    breachedLabel(type) {
      const labels = { frt: 'FRT', nrt: 'NRT', rt: 'RT' };
      return labels[type] || type.toUpperCase();
    },
  },
};
</script>

<template>
  <SettingsLayout
    :is-loading="isLoading && !metrics.total_tracked"
    :loading-message="$t('KAPTION_RESPONSE_POLICIES.REPORTS.LOADING')"
  >
    <template #header>
      <BaseSettingsHeader
        :title="$t('KAPTION_RESPONSE_POLICIES.HEADER')"
        :description="$t('KAPTION_RESPONSE_POLICIES.DESCRIPTION')"
      >
        <template #actions>
          <NextButton
            blue
            icon="i-lucide-circle-plus"
            :label="$t('KAPTION_RESPONSE_POLICIES.HEADER_BTN_TXT')"
            @click="
              $router.push({ name: 'settings_kaption_response_policies_new' })
            "
          />
        </template>
      </BaseSettingsHeader>
    </template>
    <template #body>
      <!-- Tabs -->
      <div class="mb-6 flex gap-1 border-b border-n-weak">
        <button
          class="px-4 py-2 text-sm font-medium transition-colors border-b-2 border-transparent text-n-slate-11 hover:text-n-slate-12"
          @click="navigateToPolicies"
        >
          {{ $t('KAPTION_RESPONSE_POLICIES.REPORTS.TAB_POLICIES') }}
        </button>
        <button
          class="px-4 py-2 text-sm font-medium transition-colors border-b-2 border-b-solid text-b-solid"
        >
          {{ $t('KAPTION_RESPONSE_POLICIES.REPORTS.TAB_REPORTS') }}
        </button>
      </div>

      <!-- Filters -->
      <div
        class="mb-6 grid gap-y-2 gap-x-1.5 grid-cols-[repeat(auto-fill,minmax(170px,1fr))]"
      >
        <div class="multiselect-wrap--small">
          <multiselect
            v-model="selectedDateRange"
            class="no-margin"
            :placeholder="
              $t('KAPTION_RESPONSE_POLICIES.REPORTS.FILTERS.DATE_RANGE')
            "
            label="name"
            track-by="id"
            :options="dateRangeOptions"
            :option-height="24"
            :show-labels="false"
            :allow-empty="false"
            @update:model-value="onDateRangeChange"
          />
        </div>
        <div class="multiselect-wrap--small">
          <multiselect
            v-model="selectedInbox"
            class="no-margin"
            :placeholder="$t('KAPTION_RESPONSE_POLICIES.REPORTS.FILTERS.INBOX')"
            label="name"
            track-by="id"
            :options="inboxes"
            :option-height="24"
            :show-labels="false"
            @update:model-value="onFilterChange"
          />
        </div>
        <div class="multiselect-wrap--small">
          <multiselect
            v-model="selectedTeam"
            class="no-margin"
            :placeholder="$t('KAPTION_RESPONSE_POLICIES.REPORTS.FILTERS.TEAM')"
            label="name"
            track-by="id"
            :options="teams"
            :option-height="24"
            :show-labels="false"
            @update:model-value="onFilterChange"
          />
        </div>
        <div class="multiselect-wrap--small">
          <multiselect
            v-model="selectedAgent"
            class="no-margin"
            :placeholder="$t('KAPTION_RESPONSE_POLICIES.REPORTS.FILTERS.AGENT')"
            label="name"
            track-by="id"
            :options="agents"
            :option-height="24"
            :show-labels="false"
            @update:model-value="onFilterChange"
          />
        </div>
        <div class="multiselect-wrap--small">
          <multiselect
            v-model="selectedPolicy"
            class="no-margin"
            :placeholder="
              $t('KAPTION_RESPONSE_POLICIES.REPORTS.FILTERS.POLICY')
            "
            label="name"
            track-by="id"
            :options="policies"
            :option-height="24"
            :show-labels="false"
            @update:model-value="onFilterChange"
          />
        </div>
      </div>

      <!-- Metrics Cards -->
      <div class="mb-6 grid gap-3 sm:grid-cols-3">
        <div
          class="flex flex-col items-center gap-1 rounded-xl border border-n-weak bg-n-solid-1 p-4"
        >
          <span class="text-xs font-medium text-n-slate-11">
            {{ $t('KAPTION_RESPONSE_POLICIES.REPORTS.METRICS.HIT_RATE') }}
          </span>
          <span class="text-2xl font-bold text-n-slate-12">
            {{ metrics.hit_rate || '—' }}
          </span>
        </div>
        <div
          class="flex flex-col items-center gap-1 rounded-xl border border-n-weak bg-n-solid-1 p-4"
        >
          <span class="text-xs font-medium text-n-slate-11">
            {{ $t('KAPTION_RESPONSE_POLICIES.REPORTS.METRICS.TOTAL_TRACKED') }}
          </span>
          <span class="text-2xl font-bold text-n-slate-12">
            {{ metrics.total_tracked ?? '—' }}
          </span>
        </div>
        <div
          class="flex flex-col items-center gap-1 rounded-xl border border-n-weak bg-n-solid-1 p-4"
        >
          <span class="text-xs font-medium text-n-slate-11">
            {{ $t('KAPTION_RESPONSE_POLICIES.REPORTS.METRICS.SLA_MISSES') }}
          </span>
          <span class="text-2xl font-bold text-r-solid">
            {{ metrics.number_of_misses ?? '—' }}
          </span>
        </div>
      </div>

      <!-- Breakdown by Type -->
      <div v-if="metrics.by_type" class="mb-6 grid gap-3 sm:grid-cols-3">
        <div
          class="flex flex-col items-center gap-1 rounded-xl border border-n-weak bg-n-solid-1 p-3"
        >
          <span class="text-xs font-medium text-n-slate-11">
            {{ $t('KAPTION_RESPONSE_POLICIES.REPORTS.METRICS.FRT_MISSES') }}
          </span>
          <span class="text-lg font-bold text-n-slate-12">
            {{ metrics.by_type.frt }}
          </span>
        </div>
        <div
          class="flex flex-col items-center gap-1 rounded-xl border border-n-weak bg-n-solid-1 p-3"
        >
          <span class="text-xs font-medium text-n-slate-11">
            {{ $t('KAPTION_RESPONSE_POLICIES.REPORTS.METRICS.NRT_MISSES') }}
          </span>
          <span class="text-lg font-bold text-n-slate-12">
            {{ metrics.by_type.nrt }}
          </span>
        </div>
        <div
          class="flex flex-col items-center gap-1 rounded-xl border border-n-weak bg-n-solid-1 p-3"
        >
          <span class="text-xs font-medium text-n-slate-11">
            {{ $t('KAPTION_RESPONSE_POLICIES.REPORTS.METRICS.RT_MISSES') }}
          </span>
          <span class="text-lg font-bold text-n-slate-12">
            {{ metrics.by_type.rt }}
          </span>
        </div>
      </div>

      <!-- Missed Conversations Table -->
      <div class="rounded-xl border border-n-weak bg-n-solid-1 overflow-hidden">
        <div
          class="flex items-center justify-between border-b border-n-weak px-4 py-3"
        >
          <h4 class="text-sm font-semibold text-n-slate-12">
            {{ $t('KAPTION_RESPONSE_POLICIES.REPORTS.METRICS.SLA_MISSES') }}
          </h4>
          <NextButton
            xs
            faded
            icon="i-lucide-download"
            :label="$t('KAPTION_RESPONSE_POLICIES.REPORTS.TABLE.DOWNLOAD_CSV')"
            @click="downloadCSV"
          />
        </div>

        <div v-if="missedData.length" class="overflow-x-auto">
          <table class="w-full text-left text-sm">
            <thead>
              <tr class="border-b border-n-weak bg-n-alpha-1">
                <th class="px-4 py-2 font-medium text-n-slate-11">
                  {{
                    $t(
                      'KAPTION_RESPONSE_POLICIES.REPORTS.TABLE.HEADER_CONVERSATION'
                    )
                  }}
                </th>
                <th class="px-4 py-2 font-medium text-n-slate-11">
                  {{
                    $t('KAPTION_RESPONSE_POLICIES.REPORTS.TABLE.HEADER_POLICY')
                  }}
                </th>
                <th class="px-4 py-2 font-medium text-n-slate-11">
                  {{
                    $t(
                      'KAPTION_RESPONSE_POLICIES.REPORTS.TABLE.HEADER_ASSIGNEE'
                    )
                  }}
                </th>
                <th class="px-4 py-2 font-medium text-n-slate-11">
                  {{
                    $t(
                      'KAPTION_RESPONSE_POLICIES.REPORTS.TABLE.HEADER_BREACHED'
                    )
                  }}
                </th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="item in missedData"
                :key="item.conversation_id"
                class="border-b border-n-weak last:border-none hover:bg-n-alpha-1"
              >
                <td class="px-4 py-2 text-n-slate-12">
                  <span class="font-medium">#{{ item.conversation_id }}</span>
                  <span v-if="item.contact_name" class="ml-1 text-n-slate-11">
                    {{ item.contact_name }}
                  </span>
                </td>
                <td class="px-4 py-2 text-n-slate-11">
                  {{ item.policy_name }}
                </td>
                <td class="px-4 py-2 text-n-slate-11">
                  {{ item.assignee_name || '—' }}
                </td>
                <td class="px-4 py-2">
                  <div class="flex flex-wrap gap-1">
                    <span
                      v-for="type in item.breached_types"
                      :key="type"
                      class="inline-flex rounded-md px-1.5 py-0.5 text-xs font-medium"
                      :class="breachedBadgeClass(type)"
                    >
                      {{ breachedLabel(type) }}
                    </span>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div v-else class="px-4 py-8 text-center text-sm text-n-slate-11">
          {{ $t('KAPTION_RESPONSE_POLICIES.REPORTS.TABLE.NO_DATA') }}
        </div>

        <!-- Pagination -->
        <div
          v-if="totalPages > 1"
          class="flex items-center justify-center gap-1 border-t border-n-weak px-4 py-3"
        >
          <button
            class="rounded-md px-2 py-1 text-sm text-n-slate-11 hover:bg-n-alpha-2 disabled:opacity-50"
            :disabled="page <= 1"
            @click="goToPage(page - 1)"
          >
            &lt;
          </button>
          <template v-for="p in totalPages" :key="p">
            <button
              v-if="
                p === 1 || p === totalPages || (p >= page - 1 && p <= page + 1)
              "
              class="rounded-md px-2.5 py-1 text-sm"
              :class="
                p === page
                  ? 'bg-b-solid text-white'
                  : 'text-n-slate-11 hover:bg-n-alpha-2'
              "
              @click="goToPage(p)"
            >
              {{ p }}
            </button>
            <span
              v-else-if="p === page - 2 || p === page + 2"
              class="px-1 text-n-slate-11"
            >
              ...
            </span>
          </template>
          <button
            class="rounded-md px-2 py-1 text-sm text-n-slate-11 hover:bg-n-alpha-2 disabled:opacity-50"
            :disabled="page >= totalPages"
            @click="goToPage(page + 1)"
          >
            &gt;
          </button>
        </div>
      </div>
    </template>
  </SettingsLayout>
</template>
